import React, { useState } from 'react';
import { formatDateUS } from './dateUtils';
import { QRCodeSVG } from 'qrcode.react';
import { QRPreviewModal, buildQrUrl } from '../../shared/WizardComponents';

/**
 * Reusable read-only detail header for Division 26 modules.
 * Displays item summary in a compact grid with optional QR code.
 *
 * Props:
 *  - item: data object
 *  - fields: [{key, label, type?, resolve?}]
 *  - manufacturers: [] for resolving manufacturer names
 *  - qrToken: optional QR token to display
 *  - itemName: optional name for QR preview title
 */
export const Division26DetailHeader = ({ item, fields, manufacturers = [], qrToken, itemName }) => {
  const [qrModal, setQrModal] = useState(false);

  if (!item) return null;

  const resolveValue = (field) => {
    const raw = item[field.key];
    if (field.type === 'manufacturer') {
      const mfr = manufacturers.find(m => m.id === raw);
      return mfr?.name || item.manufacturerName || '-';
    }
    if (field.type === 'date') return formatDateUS(raw);
    if (raw === null || raw === undefined || raw === '') return '-';
    return String(raw);
  };

  return (
    <>
      <div className="bg-slate-100 border border-slate-200 rounded-lg p-4 relative" data-testid="detail-header">
        <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-3 ${qrToken ? 'pr-28' : ''}`}>
          {fields.map((field) => (
            <div key={field.key} data-testid={`header-${field.key}`}>
              <dt className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">{field.label}</dt>
              <dd className="text-sm text-slate-900 font-medium mt-0.5 truncate">{resolveValue(field)}</dd>
            </div>
          ))}
        </div>
        {qrToken && (
          <button
            type="button"
            className="absolute top-3 right-3 cursor-pointer hover:opacity-70 transition-opacity"
            onClick={() => setQrModal(true)}
            data-testid="header-qr-btn"
            title="Click to enlarge QR code"
          >
            <QRCodeSVG value={buildQrUrl(qrToken)} size={96} level="L" />
          </button>
        )}
      </div>
      {qrToken && (
        <QRPreviewModal
          open={qrModal}
          onClose={() => setQrModal(false)}
          qrToken={qrToken}
          productName={itemName || item?.name}
        />
      )}
    </>
  );
};
